import type { Recipe, Article } from '../data/mockData'

const DEFAULT_TITLE = 'Kochbuch & Einkaufsliste'
const DEFAULT_DESCRIPTION = '100% kostenlos & komplett werbefrei: Dein digitales Kochbuch und deine smarte Einkaufsliste in einer App. Rezepte ohne Werbung speichern, Zutaten planen, Einkaufslisten teilen und gemeinsam organisieren.'
const DEFAULT_IMAGE = 'https://kochbuch-und-einkaufsliste.de/og-image.jpg'
const BASE_URL = 'https://kochbuch-und-einkaufsliste.de'

export interface SeoMetaOptions {
  title?: string
  description?: string
  image?: string
  url?: string
  type?: string
  jsonLd?: Record<string, any>
}

/**
 * Updates DOM head elements with page-specific SEO, Open Graph, and Structured Data.
 */
export function setPageMeta(options: SeoMetaOptions): void {
  if (typeof document === 'undefined') return

  const fullTitle = options.title
    ? `${options.title} · ${DEFAULT_TITLE}`
    : `${DEFAULT_TITLE} – 100% Kostenlos & Werbefrei`

  document.title = fullTitle

  const desc = options.description || DEFAULT_DESCRIPTION
  const img = options.image || DEFAULT_IMAGE
  const pageUrl = options.url || (typeof window !== 'undefined' ? window.location.href : BASE_URL)

  const setMetaContent = (selector: string, content: string) => {
    let meta = document.querySelector(selector) as HTMLMetaElement | null
    if (!meta) {
      meta = document.createElement('meta')
      if (selector.startsWith('meta[name=')) {
        const name = selector.match(/name="([^"]+)"/)?.[1]
        if (name) meta.setAttribute('name', name)
      } else if (selector.startsWith('meta[property=')) {
        const prop = selector.match(/property="([^"]+)"/)?.[1]
        if (prop) meta.setAttribute('property', prop)
      }
      document.head.appendChild(meta)
    }
    meta.setAttribute('content', content)
  }

  // Standard Meta
  setMetaContent('meta[name="description"]', desc)

  // Open Graph
  setMetaContent('meta[property="og:title"]', fullTitle)
  setMetaContent('meta[property="og:description"]', desc)
  setMetaContent('meta[property="og:image"]', img)
  setMetaContent('meta[property="og:url"]', pageUrl)
  setMetaContent('meta[property="og:type"]', options.type || 'website')

  // Twitter Cards
  setMetaContent('meta[name="twitter:title"]', fullTitle)
  setMetaContent('meta[name="twitter:description"]', desc)
  setMetaContent('meta[name="twitter:image"]', img)

  // Canonical Link
  let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
  if (!canonical) {
    canonical = document.createElement('link')
    canonical.setAttribute('rel', 'canonical')
    document.head.appendChild(canonical)
  }
  canonical.setAttribute('href', pageUrl)

  // Dynamic JSON-LD Structured Data
  let jsonLdScript = document.getElementById('dynamic-jsonld') as HTMLScriptElement | null
  if (options.jsonLd) {
    if (!jsonLdScript) {
      jsonLdScript = document.createElement('script')
      jsonLdScript.id = 'dynamic-jsonld'
      jsonLdScript.type = 'application/ld+json'
      document.head.appendChild(jsonLdScript)
    }
    jsonLdScript.textContent = JSON.stringify(options.jsonLd)
  } else if (jsonLdScript) {
    jsonLdScript.remove()
  }
}

/**
 * Builds standard Google-compliant Schema.org Recipe JSON-LD object.
 */
export function buildRecipeJsonLd(
  recipe: Recipe,
  articles: Article[] = [],
  categoryName?: string
): Record<string, any> {
  const ingredients = (recipe.ingredients || []).map((ing) => {
    const article = articles.find((a) => a.id === ing.articleID)
    const name = article?.name || 'Zutat'
    const unit = article?.unit ? ` ${article.unit}` : ''
    const amount = ing.amount ? `${ing.amount}${unit} ` : ''
    return `${amount}${name}`.trim()
  })

  const instructions = (recipe.steps || []).map((step, idx) => {
    const text = typeof step === 'string' ? step : step.text
    return {
      '@type': 'HowToStep',
      name: `Schritt ${idx + 1}`,
      text: text || ''
    }
  })

  const recipeImage = recipe.img && !recipe.img.startsWith('data:')
    ? recipe.img
    : DEFAULT_IMAGE

  let totalMinutes = 0
  ;(recipe.steps || []).forEach(step => {
    if (typeof step === 'object' && step?.duration && typeof step.duration === 'number') {
      totalMinutes += step.duration
    }
  })

  const jsonLd: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'Recipe',
    name: recipe.name,
    image: [recipeImage],
    description: `Rezept für ${recipe.name} aus deinem digitalen Kochbuch.`,
    recipeCategory: categoryName || 'Hauptgericht',
    inLanguage: 'de',
    recipeIngredient: ingredients,
    recipeInstructions: instructions,
    author: {
      '@type': 'Person',
      name: recipe.ownerEmail || 'Kochbuch & Einkaufsliste Community'
    }
  }

  if (totalMinutes > 0) {
    jsonLd.totalTime = `PT${totalMinutes}M`
  }

  return jsonLd
}

export function useSeoMeta() {
  return {
    setPageMeta,
    buildRecipeJsonLd
  }
}
